import type { CustomContentProps } from 'notistack';

import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import Avatar from '@mui/material/Avatar';
import { Iconify } from '@mfw/components';
import { closeSnackbar } from 'notistack';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import React from 'react';

declare module 'notistack' {
  interface VariantOverrides {
    chatMessage: {
      body: string;
      avatar?: string;
    };
  }
}

interface ChatMessageToastProps extends CustomContentProps {
  body: string;
  avatar?: string;
}

export const ChatMessageToast = React.forwardRef<
  HTMLDivElement,
  ChatMessageToastProps
>(({ id, avatar, body, message }, ref) => {
  const theme = useTheme();

  return (
    <Paper
      sx={{
        alignItems: 'flex-start',
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        boxShadow: theme.customShadows.dropdown,
        display: 'flex',
        gap: 1.5,
        maxWidth: 380,
        minWidth: 300,
        p: '12px 14px',
        pointerEvents: 'all',
      }}
      elevation={0}
      ref={ref}
    >
      <Avatar
        src={avatar || undefined}
        sx={{
          bgcolor: 'primary.lighter',
          // The glyph sits on the `lighter` tint, which is pale on every
          // ladder, so it is measured against THAT, not the page: `main` is
          // 1.5:1 there, `darker` 4.7:1.
          color: 'primary.darker',
          flexShrink: 0,
          height: 36,
          mt: '2px',
          width: 36,
        }}
      >
        <Iconify size="medium" icon="mdi:message-text-outline" />
      </Avatar>

      <Box minWidth={0} flex={1}>
        <Typography
          sx={{ color: 'text.primary', fontWeight: 600, lineHeight: 1.4 }}
          variant="subtitle2"
          noWrap
        >
          {message}
        </Typography>
        <Typography
          sx={{
            WebkitBoxOrient: 'vertical',
            WebkitLineClamp: 2,
            color: 'text.secondary',
            display: '-webkit-box',
            lineHeight: 1.4,
            mt: '2px',
            overflow: 'hidden',
          }}
          variant="body2"
        >
          {body}
        </Typography>
      </Box>

      <IconButton
        sx={{
          '&:hover': { color: 'text.primary', opacity: 1 },
          color: 'text.secondary',
          flexShrink: 0,
          mr: '-6px',
          mt: '-4px',
          opacity: 0.6,
        }}
        onClick={(e) => {
          e.stopPropagation();
          closeSnackbar(id);
        }}
        size="small"
      >
        <Iconify icon="mdi:close" size="small" />
      </IconButton>
    </Paper>
  );
});

ChatMessageToast.displayName = 'ChatMessageToast';
